/**
 * Student budget arithmetic: a monthly income-versus-spending balance, the 50/30/20
 * comparison, and the funding gap for one term.
 *
 * The 50/30/20 rule of thumb (needs 50%, wants 30%, savings and debt repayment 20% of
 * after-tax income) is a guideline, not a standard; a student's needs often exceed 50%.
 */

export const INCOME_ITEMS = ["job", "support", "other"] as const;
export const NEEDS_ITEMS = [
  "rent",
  "utilities",
  "groceries",
  "transport",
  "phone",
  "health",
  "supplies",
] as const;
export const WANTS_ITEMS = [
  "eatingOut",
  "entertainment",
  "subscriptions",
  "personal",
] as const;
export const SAVINGS_ITEMS = ["savings", "debt"] as const;

export type GroupKey = "needs" | "wants" | "savings";

export const TARGET_SHARES: Record<GroupKey, number> = {
  needs: 0.5,
  wants: 0.3,
  savings: 0.2,
};

export interface BudgetTotals {
  income: number;
  needs: number;
  wants: number;
  savings: number;
  spending: number;
  /** Income minus every outflow; negative is a shortfall. */
  balance: number;
}

export function sumAmounts(values: readonly number[]): number {
  let total = 0;
  for (const value of values) {
    total += value;
  }
  return total;
}

export function budgetTotals(parts: {
  income: readonly number[];
  needs: readonly number[];
  wants: readonly number[];
  savings: readonly number[];
}): BudgetTotals {
  const income = sumAmounts(parts.income);
  const needs = sumAmounts(parts.needs);
  const wants = sumAmounts(parts.wants);
  const savings = sumAmounts(parts.savings);
  const spending = needs + wants + savings;
  return {
    income,
    needs,
    wants,
    savings,
    spending,
    balance: income - spending,
  };
}

export interface SplitRow {
  group: GroupKey;
  amount: number;
  /** Share of income, or null when income is zero. */
  share: number | null;
  target: number;
  /** Amount minus the 50/30/20 target; positive is over it. */
  difference: number;
}

export function splitRows(totals: BudgetTotals): SplitRow[] {
  return (["needs", "wants", "savings"] as const).map((group) => {
    const amount = totals[group];
    const target = totals.income * TARGET_SHARES[group];
    return {
      group,
      amount,
      share: totals.income > 0 ? amount / totals.income : null,
      target,
      difference: amount - target,
    };
  });
}

export interface TermGap {
  /** Tuition and fees plus months of spending. */
  cost: number;
  /** Aid plus months of income. */
  resources: number;
  /** Cost minus resources; negative means the term is covered. */
  gap: number;
  yearGap: number;
  /** The gap spread over the months of the term. */
  perMonth: number;
}

export function termGap(input: {
  totals: BudgetTotals;
  months: number;
  tuition: number;
  aid: number;
  termsPerYear: number;
}): TermGap {
  const { totals, months, tuition, aid, termsPerYear } = input;
  const cost = tuition + months * totals.spending;
  const resources = aid + months * totals.income;
  const gap = cost - resources;
  return {
    cost,
    resources,
    gap,
    yearGap: gap * termsPerYear,
    perMonth: months > 0 ? gap / months : 0,
  };
}
